import React, {useMemo, useState} from 'react'
import {Link} from "react-router-dom"
import {toast} from "react-toastify"
import useAuth from "auth/useAuth"
import classNames from "classnames"
import moment from "moment"
import {romanize, daysTo} from "utils/utils"
import {plural} from "utils/text_utils"
import Application from "./Application"
import "./Week.css"

const DAYS_REGULAR = 22  // Broj dana za regularno prijavljivanje
const DAYS_LATE = 6  // Broj dana za kasno prijavljivanje (ponoć nedelja na ponedeljak)


const formatTimeRemaining = (targetDate) => {
    const now = moment()
    const target = moment(targetDate)
    const duration = moment.duration(target.diff(now))

    const totalDays = Math.floor(duration.asDays())
    const weeks = Math.floor(totalDays / 7)
    const days = totalDays % 7
    const hours = duration.hours()
    const minutes = duration.minutes()

    const units = [
        [weeks, plural(weeks, 'nedelju', 'nedelje', 'nedelja')],
        [days, plural(days, 'dan', 'dana', 'dana')],
        [hours, plural(hours, 'sat', 'sata', 'sati')],
        [minutes, plural(minutes, 'minut', 'minuta', 'minuta')],
    ]

    const parts = units.filter(([value]) => value > 0).slice(0, 2)
    return parts.map(([value, label]) => `${value} ${label}`).join(' i ')
}

export default function Week(props) {

    const {week, applications, onClick} = props
    const {username, admin} = useAuth()
    const [details, setDetails] = useState(false)

    const current = useMemo(() => {
        const days = daysTo(week)
        return days <= 0 && days > -7
    }, [week])
    
    const {regularApps, lateApps} = useMemo(() => {    
        const isRegular = app => app.regular && app.wish < 4    
        let apps = applications?.filter(app => app.week === week)
        
        apps = Array.from(
            new Map(apps?.map(app => [app.username, app])).values()
        )
                
        const regularApps = apps?.filter(isRegular)
        regularApps?.sort((a, b) => (b.score - a.score))
        const lateApps = apps?.filter(app => !isRegular(app))
        lateApps?.sort((a, b) => (b.score - a.score))
        return {regularApps, lateApps}
    }, [week, applications])
    
    const wish = useMemo(() => {
        const apps = applications?.filter(app => app.week === week && app.username === username)
        if (!apps || apps.length === 0) return 0
        else return apps[0].wish
    }, [week, applications, username])
    
    
    const active = useMemo(() => {
        return daysTo(week) >= DAYS_LATE
    }, [week])

    const [collapsed, setCollapsed] = useState(daysTo(week) <= -7)
    
    const late = useMemo(() => {
        const days = daysTo(week)
        return (days >= DAYS_LATE) && (days < DAYS_REGULAR)
    }, [week])

    const confirming = useMemo(() => {
        const days = daysTo(week)
        return days > 0 && days < DAYS_LATE
    }, [week])

    const deadline = useMemo(() => {
        const days = daysTo(week)
        if (days >= DAYS_REGULAR) {
            const deadlineDate = moment(week).subtract(DAYS_REGULAR, 'days').endOf('day')
            return formatTimeRemaining(deadlineDate)
        }
        return null
    }, [week])

    const lateDeadline = useMemo(() => {
        if (!late) return null
        const deadlineDate = moment(week).subtract(DAYS_LATE, 'days').endOf('day')
        return formatTimeRemaining(deadlineDate)
    }, [week, late])
    
    const onWeekClick = (event) => {
        if (!active) {
            if (onClick) {
                if (confirming) toast.info("Rok za prijavljivanje je istekao")
                else if (current) toast.info("Smena je trenutno aktivna")
                else toast.info("Smena je prošla")
            }
            return
        }
        if (onClick)
            onClick(week)
    }

    const toggleDetails = (event) => {
        event.stopPropagation()
        if (!active) setCollapsed(c => !c)
        setDetails(details => !details)
    }
    
    return <div className={classNames("week", {"disabled": !active && !confirming && !current, "confirming": confirming, "late": late, "current": current, "collapsed": collapsed, "clickable": onClick != null})} onClick={onWeekClick}>
        <div className="week-header">
            <div className="week-header-row">
                <h4>{moment(week).format("Do MMMM YYYY.")}</h4>
                {wish > 0 && <span className={`wish wish-${wish}`}>{romanize(wish)}</span>}
            </div>
            <div className="week-header-row">
                <span className="week-pills">
                    {current && <span className="status-label status-current">Trenutna</span>}
                    {confirming && <span className="status-label status-confirming">Sledeća</span>}
                    {confirming && <Link to="/roster" className="btn-roster" onClick={e => e.stopPropagation()}>Raspored</Link>}
                    {late && <span className="status-label status-late">Popunjavanje</span>}
                    {!active && !current && !confirming && <span className="status-label status-past">Prošla</span>}
                </span>
                <button onClick={toggleDetails} className={classNames("a details", {"active": details})}>detalji</button>
            </div>
        </div>
        {late && <div className="deadline late" title="Rok za popunjavanje je ponoć šest dana pred smenu">Može za popunjavanje još {lateDeadline}</div>}
        {deadline && <div className="deadline regular" title="Rok za prijavljivanje je ponoć tri nedelje pred smenu">Rok za regularnu prijavu ističe za {deadline}</div>}
        {!!regularApps?.length && <ol>
            {regularApps.map(app => <Application application={app} key={app.username} details={details}/>)}
        </ol>}
        {!!regularApps?.length && !!lateApps?.length && <hr/>}
        {!!lateApps?.length && <ol start={regularApps?.length+1}>
            {lateApps.map(app => <Application application={app} key={app.username} details={details}/>)}
        </ol>}
    </div>
}
